import { Link } from 'react-router-dom';

export default function EducationalSection() {
  return (
    <section className="bg-gradient-to-b from-primary-800 to-primary-900 py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="mb-16">
          <span className="inline-block px-3 py-1 bg-accent-100/20 text-accent-300 text-xs rounded-md mb-6 tracking-wide">
            /// Educational Hub
          </span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-heading text-background-50 leading-tight max-w-2xl mb-6">
            What Is Kakobuy Sheet &amp; How It Saves You Money
          </h2>
          <p className="text-sm md:text-base text-background-50/60 max-w-2xl leading-relaxed">
            A <strong className="text-background-50/80">Kakobuy spreadsheet</strong> is a community-organized product discovery tool
            that compiles trending fashion items, sneakers, and accessories into easy-to-navigate lists.
            Instead of spending hours browsing individual stores, shoppers use these curated
            spreadsheets to compare products, discover better prices, and make informed buying
            decisions. The spreadsheet format makes it incredibly efficient — you can scan hundreds
            of products in minutes, check prices side by side, and jump directly to the items
            that catch your eye.
          </p>
        </div>

        <div className="space-y-16 md:space-y-20">
          {[
            {
              num: '01',
              title: 'How Spreadsheet Shopping Works',
              description: 'Spreadsheet shopping transforms chaotic browsing into organized discovery. Each Kakobuy sheet categorizes products by type, price range, and popularity — letting you filter through thousands of curated finds in seconds. Community members contribute their best discoveries, creating a living catalog that grows richer every day.',
              image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20desk%20scene%20with%20organized%20digital%20documents%20and%20colorful%20charts%20on%20laptop%20screen%2C%20warm%20ambient%20light%20streaming%20through%20sheer%20curtains%2C%20minimal%20desk%20setup%20with%20ceramic%20mug%20and%20small%20succulent%20plant%2C%20productivity%20aesthetic%20with%20soft%20cream%20and%20warm%20beige%20tones%2C%20natural%20wood%20desk%20surface%2C%20editorial%20lifestyle%20photography%2C%20calm%20focused%20atmosphere&width=600&height=500&seq=edu-works-v3&orientation=landscape',
              link: '/blog/kakobuy-spreadsheet-ultimate-guide-2026',
              internal: true,
            },
            {
              num: '02',
              title: 'Why Thousands Use Kakobuy Spreadsheets',
              description: 'The Kakobuy spreadsheet community has grown rapidly because it solves a real problem — finding quality products without endless searching. Users save time, discover items they would have missed, and benefit from collective shopping intelligence. Whether you are looking for the latest sneaker releases or affordable streetwear, the spreadsheet format puts everything at your fingertips.',
              image: 'https://readdy.ai/api/search-image?query=Stylish%20diverse%20fashion%20items%20attractively%20displayed%20on%20warm%20wooden%20shelving%20unit%20with%20soft%20boutique%20lighting%2C%20organized%20product%20arrangement%20showing%20variety%20of%20textures%20and%20colors%2C%20cream%20and%20warm%20neutral%20tones%20throughout%2C%20editorial%20photography%20with%20shallow%20depth%20of%20field%2C%20cozy%20retail%20aesthetic%2C%20natural%20materials%20and%20woven%20baskets%20for%20storage%2C%20lifestyle%20home%20decor%20style&width=600&height=500&seq=edu-why-v3&orientation=landscape',
              link: '/blog/kakobuy-spreadsheet-ultimate-guide-2026',
              internal: true,
            },
            {
              num: '03',
              title: 'Traditional Shopping vs Spreadsheet Discovery',
              description: 'Traditional online shopping means jumping between dozens of tabs, losing track of items, and struggling to compare prices. Kakobuy spreadsheet discovery flips this — you get a single organized view of the best products, sorted and ready to explore. The comparison is stark: organized discovery beats random browsing every single time.',
              image: 'https://readdy.ai/api/search-image?query=Split%20screen%20visual%20comparison%20concept%20showing%20chaotic%20versus%20organized%20digital%20shopping%20experience%20on%20tablet%20and%20laptop%20devices%20side%20by%20side%2C%20clean%20modern%20aesthetic%20with%20warm%20neutral%20background%20wall%2C%20minimalist%20editorial%20style%2C%20soft%20natural%20window%20lighting%2C%20cream%20and%20warm%20taupe%20color%20scheme%2C%20conceptual%20lifestyle%20photography%2C%20harmonious%20balanced%20composition&width=600&height=500&seq=edu-compare-v3&orientation=landscape',
              link: '/blog/kakobuy-spreadsheet-ultimate-guide-2026',
              internal: true,
            },
          ].map((item) => (
            <div key={item.num} className="flex flex-col lg:flex-row items-center gap-8 md:gap-12">
              <div className="w-full lg:w-5/12">
                <div className="relative rounded-2xl overflow-hidden group">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-[280px] md:h-[340px] object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-primary-900/10"></div>
                </div>
              </div>
              <div className="w-full lg:w-7/12">
                <span className="text-5xl md:text-7xl font-heading text-background-50/15 leading-none block mb-2">
                  {item.num}
                </span>
                <h3 className="text-xl md:text-2xl font-heading text-background-50 mb-3">{item.title}</h3>
                <p className="text-sm text-background-50/55 leading-relaxed mb-5 max-w-lg">{item.description}</p>
                {item.internal ? (
                  <Link
                    to={item.link}
                    className="inline-flex items-center gap-2 text-sm font-medium text-background-50 border border-background-50/30 px-5 py-2.5 rounded-full hover:bg-background-50 hover:text-primary-800 transition-all whitespace-nowrap"
                  >
                    Read Full Guide <i className="ri-arrow-right-line"></i>
                  </Link>
                ) : (
                  <a
                    href={item.link}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 text-sm font-medium text-background-50 border border-background-50/30 px-5 py-2.5 rounded-full hover:bg-background-50 hover:text-primary-800 transition-all whitespace-nowrap"
                  >
                    Explore Now <i className="ri-arrow-right-line"></i>
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}